/**
 * Type → subtype → classification → lifecycle, for a document load.
 *
 * This exists because of one error. `lifecycle__v` is **required on every
 * document create** and is not implied by the type — a type may allow several —
 * so without a picker the first thing a document migration meets is Vault
 * answering "Missing required parameter: lifecycle__v", with no hint of what
 * the acceptable values are. They are labels, configured per vault, and nobody
 * can be expected to know them.
 *
 * It is one choice for the whole load rather than a column each, because in a
 * migration every document in one pipeline is the same kind. A pipeline that
 * genuinely carries per-row types maps them instead, and a mapped column wins.
 */

import { useCallback, useEffect, useState } from "react";
import { Alert, Select, Stack, Text } from "@mantine/core";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { veevaDocumentTypes, type DocumentTypeNode } from "@/shared/api/integration-browse";
import { toastApiError } from "@/shared/ui/toast";

type Level = { items: DocumentTypeNode[]; lifecycles: DocumentTypeNode[] };

const EMPTY: Level = { items: [], lifecycles: [] };

export function DocumentClassificationPicker({
  config,
  editable,
  onChange,
  connectionId,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  connectionId: string;
}) {
  const getToken = useAccessToken();
  const [types, setTypes] = useState<Level>(EMPTY);
  const [underType, setUnderType] = useState<Level>(EMPTY);
  const [underSubtype, setUnderSubtype] = useState<Level>(EMPTY);
  const [loading, setLoading] = useState(false);

  // The *names* drive the requests; the *labels* are what Vault is sent.
  const typeName = String(config.documentTypeName ?? "");
  const subtypeName = String(config.documentSubtypeName ?? "");

  const load = useCallback(
    async (body: { type_name?: string; subtype_name?: string }) => {
      const token = await getToken();
      return veevaDocumentTypes(token, connectionId, body);
    },
    [getToken, connectionId],
  );

  useEffect(() => {
    if (!connectionId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const level = await load({});
        if (!cancelled) setTypes(level);
      } catch (err) {
        toastApiError("Could not load the vault's document types", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connectionId, load]);

  useEffect(() => {
    if (!connectionId || !typeName) {
      setUnderType(EMPTY);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const level = await load({ type_name: typeName });
        if (!cancelled) setUnderType(level);
      } catch (err) {
        toastApiError("Could not load that type's subtypes", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connectionId, typeName, load]);

  useEffect(() => {
    if (!connectionId || !typeName || !subtypeName) {
      setUnderSubtype(EMPTY);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const level = await load({ type_name: typeName, subtype_name: subtypeName });
        if (!cancelled) setUnderSubtype(level);
      } catch (err) {
        toastApiError("Could not load that subtype's classifications", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connectionId, typeName, subtypeName, load]);

  const options = (nodes: DocumentTypeNode[]) =>
    nodes.map((n) => ({ value: n.name, label: n.label }));

  // The deepest level chosen is the one whose lifecycles apply.
  const lifecycles = underSubtype.lifecycles.length
    ? underSubtype.lifecycles
    : underType.lifecycles;

  const pick =
    (nameKey: string, labelKey: string, nodes: DocumentTypeNode[]) => (value: string | null) => {
      onChange(nameKey, value ?? "");
      onChange(labelKey, nodes.find((n) => n.name === value)?.label ?? "");
    };

  if (!connectionId) return null;

  return (
    <Stack gap="xs">
      <Select
        label="Document type"
        description="Applied to every document this step creates."
        placeholder={loading ? "Loading…" : "Pick a type"}
        data={options(types.items)}
        value={typeName || null}
        disabled={!editable || loading}
        searchable
        onChange={(v) => {
          pick("documentTypeName", "documentType", types.items)(v);
          // A subtype belongs to its type, and a classification to its subtype.
          onChange("documentSubtypeName", "");
          onChange("documentSubtype", "");
          onChange("documentClassificationName", "");
          onChange("documentClassification", "");
          onChange("documentLifecycle", "");
        }}
      />

      {typeName && underType.items.length > 0 && (
        <Select
          label="Subtype"
          placeholder="Pick a subtype"
          data={options(underType.items)}
          value={subtypeName || null}
          disabled={!editable}
          searchable
          clearable
          onChange={(v) => {
            pick("documentSubtypeName", "documentSubtype", underType.items)(v);
            onChange("documentClassificationName", "");
            onChange("documentClassification", "");
          }}
        />
      )}

      {subtypeName && underSubtype.items.length > 0 && (
        <Select
          label="Classification"
          placeholder="Pick a classification"
          data={options(underSubtype.items)}
          value={String(config.documentClassificationName ?? "") || null}
          disabled={!editable}
          searchable
          clearable
          onChange={pick(
            "documentClassificationName",
            "documentClassification",
            underSubtype.items,
          )}
        />
      )}

      {typeName && (
        <Select
          label="Lifecycle"
          description="Required on every document. A type can allow more than one, so it cannot be guessed."
          placeholder={lifecycles.length ? "Pick a lifecycle" : "None offered for this type"}
          data={lifecycles.map((n) => ({ value: n.label, label: n.label }))}
          value={String(config.documentLifecycle ?? "") || null}
          disabled={!editable || lifecycles.length === 0}
          searchable
          onChange={(v) => onChange("documentLifecycle", v ?? "")}
        />
      )}

      {typeName && !config.documentLifecycle && lifecycles.length > 0 && (
        <Alert color="orange" variant="light">
          Pick a lifecycle — Vault refuses every row without one.
        </Alert>
      )}

      <Text size="xs" c="dimmed">
        These apply to every document in this load. If your data carries a type per row, map a
        column to{" "}
        <Text span ff="monospace">
          type__v
        </Text>{" "}
        instead — a mapped column wins.
      </Text>
    </Stack>
  );
}
